import useVehicleOrders from "../../data/useVehicleOrders";
import Card from "../shared/Card/Card";
import {
  DocumentTextIcon,
  ArrowUturnLeftIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import Tooltip from "../shared/Tooltip";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IPart } from "../../models/Part";
import usePartInvoice from "../../data/usePartInvoice";
import useInvoiceMutations from "../../data/useInvoiceMutations";
import CreditInvoiceModal from "../invoices/CreditInvoiceModal";
import formatDateTime from "../../utils/formatDateTime";

function InvoiceActions({ partId }: { partId: string }) {
  const { t } = useTranslation("vehicles");
  const { invoice } = usePartInvoice(partId);
  const { downloadInvoice } = useInvoiceMutations();
  const navigate = useNavigate();

  const [creditModalOpen, setCreditModalOpen] = useState(false);


  return (
    <div className="mt-3 flex items-center gap-2">
      <CreditInvoiceModal
        open={creditModalOpen}
        setOpen={setCreditModalOpen}
        invoice={invoice.data?.data ?? null}
      />
      <Tooltip content={t("View Invoice")}>
        <button
          type="button"
          onClick={() => navigate(`/invoices/${invoice.data?.data.id}`)}
          className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-600/40"
        >
          <DocumentTextIcon className="h-5 w-5" />
        </button>
      </Tooltip>

      <Tooltip content={t("Return Part")}>
        <button
          type="button"
          onClick={() => setCreditModalOpen(true)}
          className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-600/40"
        >
          <ArrowUturnLeftIcon className="h-5 w-5" />
        </button>
      </Tooltip>

      <Tooltip content={t("Download Invoice")}>
        <button
          type="button"
          onClick={() => {
            downloadInvoice(invoice.data?.data.id!);
          }}
          className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-600/40"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
        </button>
      </Tooltip>

      {/* subtle affordance on hover */}
      <div className="ml-auto h-2 w-2 rounded-full bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}


export default function VehicleOrders({
  vehicleId,
  collapsible = true,
}: {
  vehicleId: string;
  collapsible?: boolean;
}) {
  const { t } = useTranslation("vehicles");
  const { orders: orderResponse } = useVehicleOrders(vehicleId);

  const [orders, setOrders] = useState<IPart[]>([]);

  useEffect(() => {
    if (!orderResponse.isLoading && orderResponse.data) {
      setOrders(orderResponse.data.data || []);
    }
  }, [orderResponse.data, orderResponse.isLoading]);

  

  return (
    <Card title={t("Vehicle Orders")} bodyClassName="p-4" collapsible={collapsible}>
      {!orders?.length ? (
        <div className="text-sm text-gray-500">{t("No orders found.")}</div>
      ) : (
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {orders.map((order: IPart) => (
            <div
              key={order.id}
              className="group rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow transition-shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-md font-semibold text-gray-900">
                    {order.title}
                  </div>
                  <div className="mt-0.5 text-sm text-gray-500">
                    {t("Sold Date: {{date}}", { date: formatDateTime(order.soldDate) })}
                  </div>
                </div>
              </div>

              <InvoiceActions partId={order.id} />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
